import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { FlashMessagesService } from 'flash-messages-angular';
import { RouteService } from '../services/route.service';

@Injectable()
export class GlobalErrorsHandler extends ErrorHandler {
  private handledErrors = new Set<any>();

  public ignore = false;

  constructor(private flashMessage: FlashMessagesService, private routeService: RouteService) {
    super();
  }

  // #region Public Methods

  public async handleError(rejection: any): Promise<void> {
    try {
      const error = this.getError(rejection);

      if (this.shouldIgnore(error)) {
        return;
      }

      // Check if particular error has been already handled explicitly from component
      if (this.isAlreadyHandled(error)) {
        return;
      }

      const promises = [];

      promises.push(this.log(error));
      promises.push(this.showMessage(error));

      this.markAsHandled(error);

      await Promise.all(promises);
    } catch (innerError) {
      console.error('Error Handling Exception', innerError);
      console.error(rejection);
    } finally {
      // Clear errors in new execution context (macrotask)
      setTimeout(() => {
        this.handledErrors.clear();
      });
    }
  }

  public isHttpError(res: any) {
    return (
      (res instanceof HttpErrorResponse && res.status >= 500) || // Internal Server Error
      (res instanceof HttpErrorResponse && !res.status) || // No Internet Connection
      (res instanceof HttpResponse && res.body && res.body.success === false) ||
      (res instanceof HttpResponse && !res.status)
    );
  }

  public displayError(message: string) {
    setTimeout(() => {
      this.flashMessage.show(message || 'Error', {
        cssClass: 'alert-danger',
        timeout: 5000
      });

      setTimeout(() => {
        this.routeService.scrollToTop();
      }, 50);
    }, 10);
  }

  public getDisplayMessage(error: any): string {
    let message = '';

    if (this.isHttpError(error)) {
      message = this.getHttpErrorMessage(error);
    } else if (typeof error === 'string') {
      message = error;
    }

    return message;
  }

  public hasNoInternetAccess(res: any): boolean {
    return (res instanceof HttpResponse && !res.status)
        || (res instanceof HttpErrorResponse && !res.status);
  }

  public getHttpErrorMessage(res: HttpErrorResponse): string {
    if (this.hasNoInternetAccess(res)) {
      return 'Please check your internet connection and try again.';
    }
    else if (res instanceof HttpResponse && res.body && res.body.success === false) {
      const error = res.body || {};
      const message = error.code && error.message || 'Internal Server Error';
      return message;
    }
    else if (res instanceof HttpErrorResponse && res.status >= 500) {
      const error = res.error || {};
      const message = error.code && error.message || 'Internal Server Error';
      return message;
    }
    else {
      return '';
    }
  }

  public markAsHandled(error: any, value = true): void {
    if (value) {
      this.handledErrors.add(error);
    } else {
      this.handledErrors.delete(error);
    }
  }

  public isAlreadyHandled(error: any): boolean {
    const result = error ? this.handledErrors.has(error) : false;
    return result;
  }

  // #endregion

  // #region Private Methods

  private log(error: any, options: { debug?: boolean } = {}): void {
    let message: string = error ? error.message : error;

    if (this.isHttpError(error)) {
      message = `Server Error: ${this.getHttpErrorMessage(error)}`;
    }

    if (!message) {
      message = this.getDisplayMessage(error);
    }

    if (!message) {
      message = 'An unknown error occurred in an Angular event.';
    }

    // Log the exception using the built-in logger.
    try {
      if (options.debug) {
        console.debug(message, { exception: error });
      } else {
        console.error(message, { exception: error });
      }
    } catch (ex) {
      console.error(message, error);
      console.error('Unhandled Exception', ex);
    }
  }

  private async showMessage(error: any): Promise<void> {
    try {
      const message = this.getDisplayMessage(error);

      if (message) {
        try {
          if (!this.isAlreadyHandled(error)) {
            this.displayError(message);
          }
        } catch (err) {
          console.warn('There was a problem alerting the user to an Angular error.', err);
        }
      }
    } catch (ex) {
      console.warn('There was a problem alerting the user to an Angular error; falling back to a standard alert().', ex);

      if (!this.isAlreadyHandled(error)) {
        alert('An error has occurred; please try again.');
      }
    }
  }

  private shouldIgnore(error: any): boolean {
    if (this.ignore) {
      return true;
    }

    if (!error) {
      return false;
    }

    const httpError = error instanceof HttpErrorResponse ? error : null;

    if (httpError) {
      // skip logging authentication exceptions
      if (httpError.status === 401 || httpError.status === 403) {
        return true;
      }
    }

    return false;
  }

  private getError(error: any): any {
    if (error && error.rejection) {
      return error.rejection;
    } else {
      return error;
    }
  }

  // #endregion
}
